import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Thermometer,
  Calendar,
  AlertCircle,
  Clock,
  MessageSquare,
  Inbox,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

type AbsenceKind = 'illness' | 'vacation'

interface KrankenstandEvent {
  id: string
  employeeId: number
  employeeName: string
  planningType: string
  kind: AbsenceKind
  from: number
  to: number
}

interface CategorizedTicket {
  id: number
  title: string
  statusId: number
  statusName: string
  assigneeId: number | null
  assigneeName: string | null
  dueDate: number | null
  updatedAt: number | null
  statusChangedAt: number | null
}

interface KrankenstandResponse {
  view: 'week' | 'month'
  from: string
  to: string
  events: KrankenstandEvent[]
  tickets: {
    dueDuringAbsence: CategorizedTicket[]
    customerReaction: CategorizedTicket[]
    otherOpen: CategorizedTicket[]
  }
  settings: { minAbsenceWorkdays: number; customerReactionStatusIds: number[] }
  tanssBaseUrl: string
  error?: string
  ticketError?: string
  _debug?: Record<string, unknown>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DE_WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const DE_MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function parseIso(s: string): Date {
  return new Date(`${s}T00:00:00`)
}

function addDays(iso: string, n: number): string {
  const d = parseIso(iso)
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

function addMonths(iso: string, n: number): string {
  const d = parseIso(iso)
  d.setMonth(d.getMonth() + n)
  return d.toISOString().slice(0, 10)
}

function getWeekStart(iso: string): Date {
  const d = parseIso(iso)
  const dow = d.getDay()
  const offset = dow === 0 ? -6 : 1 - dow
  d.setDate(d.getDate() + offset)
  return d
}

function getDaysInWeek(iso: string): Date[] {
  const monday = getWeekStart(iso)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    return d
  })
}

function getMonthGrid(iso: string): Date[][] {
  const ref = parseIso(iso)
  const year = ref.getFullYear()
  const month = ref.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const startDow = firstDay.getDay()
  const offset = startDow === 0 ? 6 : startDow - 1
  const weeks: Date[][] = []
  let cursor = new Date(firstDay)
  cursor.setDate(cursor.getDate() - offset)
  while (cursor <= lastDay || cursor.getDay() !== 1) {
    const week: Date[] = []
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor))
      cursor.setDate(cursor.getDate() + 1)
    }
    weeks.push(week)
  }
  return weeks
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
}

function formatTs(ts: number | null): string {
  if (!ts) return '–'
  return new Date(ts * 1000).toLocaleDateString('de-DE')
}

function eventCoversDay(event: KrankenstandEvent, day: Date): boolean {
  const dayStart = Math.floor(day.getTime() / 1000)
  const dayEnd = dayStart + 86399
  return event.from <= dayEnd && event.to >= dayStart
}

function absenceLabel(planningType: string): string {
  switch (planningType) {
    case 'ILLNESS': return 'Krank'
    case 'VACATION': return 'Urlaub'
    default: return planningType
  }
}

function buildTanssTicketUrl(tanssBaseUrl: string, ticketId: number): string {
  const base = tanssBaseUrl.replace(/\/$/, '')
  return `${base}/index.php?section=bug&sub=view&bugID=${ticketId}`
}

function getIsoWeekNumber(d: Date): number {
  const date = new Date(d)
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7))
  const week1 = new Date(date.getFullYear(), 0, 4)
  return (
    1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
  )
}

// ─── Calendar: Week View ─────────────────────────────────────────────────────

function WeekCalendar({ events, dateIso }: { events: KrankenstandEvent[]; dateIso: string }) {
  const days = getDaysInWeek(dateIso)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const employeeIds = [...new Set(events.map((e) => e.employeeId))]
  const employeeNames = new Map(events.map((e) => [e.employeeId, e.employeeName]))

  if (employeeIds.length === 0) {
    return (
      <EmptyState
        icon={<Calendar className="w-8 h-8" />}
        title="Keine Abwesenheiten in dieser Woche"
        description="Alle Mitarbeiter sind in diesem Zeitraum verfügbar."
      />
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="text-left px-3 py-2 text-body-sm text-gray-500 w-40 border-b border-gray-200">
              Mitarbeiter
            </th>
            {days.map((day, i) => {
              const isToday = day.getTime() === today.getTime()
              const isWeekend = day.getDay() === 0 || day.getDay() === 6
              return (
                <th
                  key={i}
                  className={[
                    'px-2 py-2 text-center border-b border-gray-200 min-w-[60px]',
                    isToday ? 'bg-brand-50 font-semibold text-brand-700' : 'text-gray-600',
                    isWeekend ? 'text-gray-400' : '',
                  ].join(' ')}
                >
                  <div className="text-caption">{DE_WEEKDAYS[i]}</div>
                  <div className="text-caption">{formatDayLabel(day)}</div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {employeeIds.map((empId) => {
            const empEvents = events.filter((e) => e.employeeId === empId)
            return (
              <tr key={empId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-body font-medium text-gray-900 truncate max-w-[160px]">
                  {employeeNames.get(empId)}
                </td>
                {days.map((day, i) => {
                  const dayEvent = empEvents.find((e) => eventCoversDay(e, day))
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6
                  return (
                    <td
                      key={i}
                      className={['px-1 py-1 text-center', isWeekend ? 'bg-gray-50' : ''].join(' ')}
                    >
                      {dayEvent && (
                        <div
                          title={`${employeeNames.get(empId)} – ${absenceLabel(dayEvent.planningType)}`}
                          className={[
                            'rounded text-caption px-1 py-0.5 truncate',
                            dayEvent.kind === 'illness'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700',
                          ].join(' ')}
                        >
                          {absenceLabel(dayEvent.planningType)}
                        </div>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Calendar: Month View ─────────────────────────────────────────────────────

function MonthCalendar({ events, dateIso }: { events: KrankenstandEvent[]; dateIso: string }) {
  const ref = parseIso(dateIso)
  const currentMonth = ref.getMonth()
  const weeks = getMonthGrid(dateIso)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return (
    <div>
      <div className="grid grid-cols-7 border-b border-gray-200 mb-1">
        {DE_WEEKDAYS.map((d) => (
          <div key={d} className="text-center text-caption text-gray-500 py-2 font-medium">
            {d}
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-1">
            {week.map((day, di) => {
              const isCurrentMonth = day.getMonth() === currentMonth
              const isToday = day.getTime() === today.getTime()
              const isWeekend = day.getDay() === 0 || day.getDay() === 6
              const dayEvents = events.filter((e) => eventCoversDay(e, day))
              return (
                <div
                  key={di}
                  className={[
                    'min-h-[72px] rounded p-1',
                    isCurrentMonth ? 'bg-white border border-gray-100' : 'bg-gray-50 border border-gray-100 opacity-50',
                    isToday ? 'border-brand-400 bg-brand-50' : '',
                    isWeekend ? 'bg-gray-50' : '',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'text-caption font-medium mb-1',
                      isToday ? 'text-brand-700' : isCurrentMonth ? 'text-gray-700' : 'text-gray-400',
                    ].join(' ')}
                  >
                    {day.getDate()}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <div
                        key={ev.id}
                        title={`${ev.employeeName} – ${absenceLabel(ev.planningType)}`}
                        className={[
                          'text-caption rounded px-1 py-0.5 truncate',
                          ev.kind === 'illness'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-orange-100 text-orange-700',
                        ].join(' ')}
                      >
                        {ev.employeeName.split(' ')[0]}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-caption text-gray-400">+{dayEvents.length - 3}</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Navigation ──────────────────────────────────────────────────────────────

function CalendarNav({
  view, dateIso, onPrev, onNext, onToday,
}: {
  view: 'week' | 'month'
  dateIso: string
  onPrev: () => void
  onNext: () => void
  onToday: () => void
}) {
  let label: string
  if (view === 'week') {
    const days = getDaysInWeek(dateIso)
    const start = days[0]!
    const end = days[6]!
    label = `KW ${getIsoWeekNumber(start)} – ${start.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} bis ${end.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}`
  } else {
    const ref = parseIso(dateIso)
    label = `${DE_MONTHS[ref.getMonth()]} ${ref.getFullYear()}`
  }

  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={onPrev} className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label="Zurück">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <span className="text-body font-medium text-gray-700 min-w-[220px] text-center">{label}</span>
      <button type="button" onClick={onNext} className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label="Weiter">
        <ChevronRight className="w-4 h-4" />
      </button>
      <button type="button" onClick={onToday} className="text-caption text-brand-600 hover:text-brand-700 ml-2 underline">
        Heute
      </button>
    </div>
  )
}

// ─── Ticket Row ───────────────────────────────────────────────────────────────

function TicketRow({ ticket, tanssBaseUrl }: { ticket: CategorizedTicket; tanssBaseUrl: string }) {
  const url = tanssBaseUrl ? buildTanssTicketUrl(tanssBaseUrl, ticket.id) : null
  return (
    <tr
      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
      onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
    >
      <td className="px-3 py-2 text-body text-gray-900 max-w-xs">
        <span className={url ? 'hover:text-brand-700 hover:underline' : ''}>{ticket.title}</span>
      </td>
      <td className="px-3 py-2 text-caption text-gray-600 whitespace-nowrap">
        {ticket.assigneeName ?? '–'}
      </td>
      <td className="px-3 py-2 text-caption text-gray-600 whitespace-nowrap">
        {ticket.dueDate ? formatTs(ticket.dueDate) : <span className="text-gray-400">Keine Fälligkeit</span>}
      </td>
      <td className="px-3 py-2">
        <Badge variant="neutral" size="sm">{ticket.statusName}</Badge>
      </td>
    </tr>
  )
}

// ─── Ticket Category Section ─────────────────────────────────────────────────

function TicketCategory({
  title,
  icon,
  tickets,
  tanssBaseUrl,
  defaultOpen,
  emptyLabel,
  colorClass,
}: {
  title: string
  icon: React.ReactNode
  tickets: CategorizedTicket[]
  tanssBaseUrl: string
  defaultOpen: boolean
  emptyLabel: string
  colorClass: string
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 text-left transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-gray-400 flex-shrink-0 w-4">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
        <span className={`flex-shrink-0 ${colorClass}`}>{icon}</span>
        <span className="flex-1 text-body font-medium text-gray-900">{title}</span>
        <Badge variant={tickets.length > 0 ? 'warning' : 'neutral'} size="sm">
          {tickets.length} Ticket{tickets.length !== 1 ? 's' : ''}
        </Badge>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {tickets.length === 0 ? (
            <div className="px-4 py-6">
              <EmptyState
                icon={<Inbox className="w-6 h-6" />}
                title={emptyLabel}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-caption text-gray-500 font-medium">Betreff</th>
                    <th className="text-left px-3 py-2 text-caption text-gray-500 font-medium whitespace-nowrap">Techniker</th>
                    <th className="text-left px-3 py-2 text-caption text-gray-500 font-medium whitespace-nowrap">Fälligkeit</th>
                    <th className="text-left px-3 py-2 text-caption text-gray-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <TicketRow key={t.id} ticket={t} tanssBaseUrl={tanssBaseUrl} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Debug Section ────────────────────────────────────────────────────────────

function DebugSection({ debug }: { debug: Record<string, unknown> }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden text-left">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-caption text-gray-500"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        TANSS-Diagnosedaten anzeigen
      </button>
      {open && (
        <pre className="p-3 text-caption text-gray-600 overflow-x-auto bg-white">
          {JSON.stringify(debug, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DispoKrankenstandPage() {
  const [view, setView] = useState<'week' | 'month'>('week')
  const [dateIso, setDateIso] = useState<string>(todayIso)

  const { data, isLoading, isError, error } = useQuery<KrankenstandResponse>({
    queryKey: ['dispo-krankenstand', view, dateIso],
    queryFn: () =>
      apiClient.get(`/dispo/krankenstand?view=${view}&date=${dateIso}`).then((r) => r.data),
    refetchInterval: 5 * 60 * 1000,
    retry: false,
  })

  function handlePrev() {
    if (view === 'week') setDateIso((d) => addDays(d, -7))
    else setDateIso((d) => addMonths(d, -1))
  }

  function handleNext() {
    if (view === 'week') setDateIso((d) => addDays(d, 7))
    else setDateIso((d) => addMonths(d, 1))
  }

  const tanssBaseUrl = data?.tanssBaseUrl ?? ''
  const tickets = data?.tickets ?? { dueDuringAbsence: [], customerReaction: [], otherOpen: [] }
  const totalTickets = tickets.dueDuringAbsence.length + tickets.customerReaction.length + tickets.otherOpen.length

  return (
    <div className="space-y-6">
      <PageHeader
        title="Krankenstand"
        icon={<Thermometer className="w-5 h-5 text-red-400" />}
        description="Abwesenheiten aus TANSS – Kalender und offene Tickets abwesender Mitarbeiter"
      />

      {/* Calendar Section */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Tabs value={view} onValueChange={(v) => setView(v as 'week' | 'month')}>
              <TabsList>
                <TabsTrigger value="week">Woche</TabsTrigger>
                <TabsTrigger value="month">Monat</TabsTrigger>
              </TabsList>
            </Tabs>
            <CalendarNav
              view={view}
              dateIso={dateIso}
              onPrev={handlePrev}
              onNext={handleNext}
              onToday={() => setDateIso(todayIso())}
            />
          </div>

          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-red-200 inline-block" />
              <span className="text-caption text-gray-500">Krank</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-orange-200 inline-block" />
              <span className="text-caption text-gray-500">Urlaub</span>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {isLoading && <LoadingState message="Abwesenheiten werden geladen…" />}

          {isError && (
            <ErrorState
              title="Kalender nicht verfügbar"
              description={error instanceof Error ? error.message : 'Unbekannter Fehler'}
            />
          )}

          {data?.error && !data.events?.length && (
            <div>
              <ErrorState title="TANSS nicht verfügbar" description={data.error} />
              {data._debug && <DebugSection debug={data._debug} />}
            </div>
          )}

          {data && !isLoading && !isError && (
            <>
              <Tabs value={view}>
                <TabsContent value="week">
                  <WeekCalendar events={data.events} dateIso={dateIso} />
                </TabsContent>
                <TabsContent value="month">
                  <MonthCalendar events={data.events} dateIso={dateIso} />
                </TabsContent>
              </Tabs>
              {data._debug && <DebugSection debug={data._debug} />}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Ticket Sections */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-h3 text-gray-800">
            Offene Tickets abwesender Mitarbeiter
          </h2>
          {!isLoading && data && (
            <span className="text-caption text-gray-500">
              {totalTickets} Ticket{totalTickets !== 1 ? 's' : ''} gesamt
            </span>
          )}
        </div>

        {isLoading && (
          <Card>
            <Card.Body>
              <LoadingState message="Tickets werden geladen…" />
            </Card.Body>
          </Card>
        )}

        {data?.ticketError && (
          <div className="mb-3 flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="text-body">{data.ticketError}</span>
          </div>
        )}

        {!isLoading && data && !data.error && !data.ticketError && totalTickets === 0 && data.events.length === 0 && (
          <Card>
            <Card.Body>
              <EmptyState
                icon={<Calendar className="w-8 h-8" />}
                title="Keine Abwesenheiten in diesem Zeitraum"
                description="Alle Mitarbeiter sind verfügbar – keine Tickets zur Priorisierung."
              />
            </Card.Body>
          </Card>
        )}

        {!isLoading && data && !data.error && (
          <div className="space-y-3">
            <TicketCategory
              title="Fälligkeit innerhalb der Abwesenheit"
              icon={<Clock className="w-4 h-4" />}
              tickets={tickets.dueDuringAbsence}
              tanssBaseUrl={tanssBaseUrl}
              defaultOpen={true}
              emptyLabel="Keine Tickets mit Fälligkeit innerhalb einer Abwesenheit"
              colorClass="text-red-500"
            />
            <TicketCategory
              title="Kundenreaktion erfolgt"
              icon={<MessageSquare className="w-4 h-4" />}
              tickets={tickets.customerReaction}
              tanssBaseUrl={tanssBaseUrl}
              defaultOpen={true}
              emptyLabel="Keine Tickets mit Kundenreaktion"
              colorClass="text-orange-500"
            />
            <TicketCategory
              title="Weitere offene Tickets"
              icon={<Inbox className="w-4 h-4" />}
              tickets={tickets.otherOpen}
              tanssBaseUrl={tanssBaseUrl}
              defaultOpen={false}
              emptyLabel="Keine weiteren offenen Tickets"
              colorClass="text-gray-400"
            />
          </div>
        )}
      </div>
    </div>
  )
}
